import type {
    Child,
    LeaveRequest,
    LeaveRequestAttachment,
    LeaveRequestAttachmentData,
    LeaveRequestData,
    LeaveRequestPatch,
    MedicalRequest,
    MedicalRequestData,
    MedicalRequestPatch,
    ParentSession,
    PickupRequest,
    PickupRequestData,
    Profile,
    ProfilePatch,
    SchoolYear,
} from "@spork/s4p-server-sdk";
import { Platform } from "react-native";
import * as FileSystem from "expo-file-system/legacy";
import * as IntentLauncher from "expo-intent-launcher";

import { getConfig } from "@/getConfig";
import { ensureFeathersReady, getFeathersClient } from "@/lib/feathers/client";
import { authStorage } from "@/lib/feathers/storage";
import { log } from "@/logger";
import { openFilePreview } from "file-preview";
// Import i18n from i18n/i18n.ts to avoid circular dependency via queries.ts and apiSlice.ts
import { default as i18n, isAppLanguage, type AppLanguage } from "../i18n/i18n";

/** Language query value for s4p-server localized profile field labels. */
function getApiLanguage(): AppLanguage {
    return isAppLanguage(i18n.language) ? i18n.language : "en";
}

function profileLangQuery() {
    return { lang: getApiLanguage() };
}

export type PatchProfileArgs = {
    id: number;
    data: ProfilePatch;
};

export type ResubmitLeaveRequestArgs = {
    id: number;
    message?: string | null;
};

export type UpdateLeaveRequestEndDateArgs = {
    id: number;
    date_to: string;
    time_to?: string | null;
};

export type CreateLeaveRequestAttachmentArgs = LeaveRequestAttachmentData;

export type UpdateMedicalRequestArgs = {
    id: number;
    note: string;
};

export type ApiQuery = {
    getChildren: () => Promise<Child[]>;
    getLeaveRequests: (data?: unknown) => Promise<LeaveRequest[]>;
    getLeaveRequestsForChildren: (data?: unknown) => Promise<LeaveRequest[]>;
    getLeaveRequest: (id: number) => Promise<LeaveRequest>;
    createLeaveRequest: (data: LeaveRequestData) => Promise<LeaveRequest[]>;
    cancelLeaveRequest: (id: number) => Promise<LeaveRequest>;
    resubmitLeaveRequest: (
        args: ResubmitLeaveRequestArgs,
    ) => Promise<LeaveRequest>;
    updateLeaveRequestEndDate: (
        args: UpdateLeaveRequestEndDateArgs,
    ) => Promise<LeaveRequest>;
    createLeaveRequestAttachment: (
        data: CreateLeaveRequestAttachmentArgs,
    ) => Promise<LeaveRequestAttachment>;
    notifyAddedLeaveRequestAttachment: (ids: number[]) => Promise<void>;
    removeLeaveRequestAttachment: (
        id: number,
    ) => Promise<LeaveRequestAttachment>;
    downloadLeaveRequestAttachment: (
        attachment: LeaveRequestAttachment,
    ) => Promise<void>;
    getMedicalRequestsForChildren: (data?: unknown) => Promise<MedicalRequest[]>;
    getMedicalRequest: (id: number) => Promise<MedicalRequest>;
    createMedicalRequest: (data: MedicalRequestData) => Promise<MedicalRequest>;
    updateMedicalRequest: (args: UpdateMedicalRequestArgs) => Promise<MedicalRequest>;
    revokeMedicalRequest: (id: number) => Promise<MedicalRequest>;
    getPickupRequestsForChildren: (data?: unknown) => Promise<PickupRequest[]>;
    createPickupRequest: (data: PickupRequestData) => Promise<PickupRequest[]>;
    revokePickupRequest: (id: number) => Promise<PickupRequest>;
    getSchoolYear: (userId: number) => Promise<SchoolYear>;
    getProfiles: () => Promise<Profile[]>;
    getProfile: (id: number) => Promise<Profile>;
    patchProfile: (args: PatchProfileArgs) => Promise<Profile>;
    getParentSessions: () => Promise<ParentSession[]>;
    revokeParentSession: (id: number) => Promise<ParentSession>;
    revokeAllParentSessions: () => Promise<ParentSession[]>;
};

export const query: ApiQuery = {
    getChildren: async (): Promise<Child[]> => {
        await ensureFeathersReady();
        const children = await getFeathersClient().service("child").find();
        log.debug("getChildren", JSON.stringify(children));
        return children;
    },
    getLeaveRequests: async (data?: unknown): Promise<LeaveRequest[]> => {
        const childId = data as number;
        await ensureFeathersReady();
        const leaveRequests = await getFeathersClient()
            .service("leave-request")
            .find({ query: { child_id: childId } });
        log.debug("getLeaveRequests", JSON.stringify(leaveRequests));
        return leaveRequests;
    },
    getLeaveRequestsForChildren: async (
        data?: unknown,
    ): Promise<LeaveRequest[]> => {
        const childIds = data as number[];

        if (childIds.length === 0) {
            return [];
        }

        await ensureFeathersReady();
        const leaveRequestsByChild = await Promise.all(
            childIds.map((childId) =>
                getFeathersClient()
                    .service("leave-request")
                    .find({ query: { child_id: childId } }),
            ),
        );
        const leaveRequests = leaveRequestsByChild.flat();
        log.debug("getLeaveRequestsForChildren", JSON.stringify(leaveRequests));
        return leaveRequests;
    },
    getLeaveRequest: async (id: number): Promise<LeaveRequest> => {
        await ensureFeathersReady();
        const leaveRequest = await getFeathersClient()
            .service("leave-request")
            .get(id);
        log.debug("getLeaveRequest", JSON.stringify(leaveRequest));
        return leaveRequest;
    },
    createLeaveRequest: async (data: LeaveRequestData) => {
        await ensureFeathersReady();
        const leaveRequest = await getFeathersClient()
            .service("leave-request")
            .create(data);
        log.debug("createLeaveRequest", JSON.stringify(leaveRequest));
        return Array.isArray(leaveRequest) ? leaveRequest : [leaveRequest];
    },
    cancelLeaveRequest: async (id: number) => {
        await ensureFeathersReady();
        const leaveRequest = await getFeathersClient()
            .service("leave-request")
            .remove(id);
        log.debug("cancelLeaveRequest", JSON.stringify(leaveRequest));
        return leaveRequest;
    },
    resubmitLeaveRequest: async ({ id, message }: ResubmitLeaveRequestArgs) => {
        await ensureFeathersReady();
        const data: LeaveRequestPatch = {
            action: "resubmit",
            ...(message !== undefined ? { message } : {}),
        };
        const leaveRequest = await getFeathersClient()
            .service("leave-request")
            .patch(id, data);
        log.debug("resubmitLeaveRequest", JSON.stringify(leaveRequest));
        return leaveRequest;
    },
    updateLeaveRequestEndDate: async ({
        id,
        date_to,
        time_to,
    }: UpdateLeaveRequestEndDateArgs) => {
        await ensureFeathersReady();
        const data: LeaveRequestPatch = {
            action: "update_end_date",
            date_to,
            ...(time_to !== undefined ? { time_to } : {}),
        };
        const leaveRequest = await getFeathersClient()
            .service("leave-request")
            .patch(id, data);
        log.debug("updateLeaveRequestEndDate", JSON.stringify(leaveRequest));
        return leaveRequest;
    },

    createLeaveRequestAttachment: async (
        data: CreateLeaveRequestAttachmentArgs,
    ) => {
        // Prefer REST over Socket.IO — base64 payloads easily exceed the default
        // Socket.IO maxHttpBufferSize (~1 MiB).
        await ensureFeathersReady();
        const token = await authStorage.getItem("feathers-jwt");

        if (!token) {
            throw new Error("Missing authentication token.");
        }

        const serverUrl = getConfig("s4pServerUrl")?.replace(/\/$/, "");

        if (!serverUrl) {
            throw new Error("s4pServerUrl is not configured");
        }

        const response = await fetch(`${serverUrl}/leave-request-attachment`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(data),
        });

        const body = (await response.json().catch(() => null)) as
            | LeaveRequestAttachment
            | { message?: string }
            | null;

        if (!response.ok) {
            const message =
                body &&
                typeof body === "object" &&
                "message" in body &&
                typeof body.message === "string"
                    ? body.message
                    : `Attachment upload failed (${response.status}).`;
            throw new Error(message);
        }

        log.debug("createLeaveRequestAttachment", JSON.stringify(body));
        return body as LeaveRequestAttachment;
    },

    notifyAddedLeaveRequestAttachment: async (ids: number[]) => {
        await ensureFeathersReady();
        const token = await authStorage.getItem("feathers-jwt");

        if (!token) {
            throw new Error("Missing authentication token.");
        }

        const serverUrl = getConfig("s4pServerUrl")?.replace(/\/$/, "");

        if (!serverUrl) {
            throw new Error("s4pServerUrl is not configured");
        }

        const response = await fetch(`${serverUrl}/leave-request-attachment`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                "X-Service-Method": "notifyAdded",
            },
            body: JSON.stringify({ ids }),
        });

        if (!response.ok) {
            const body = (await response.json().catch(() => null)) as
                | { message?: string }
                | null;
            const message =
                body &&
                typeof body === "object" &&
                typeof body.message === "string"
                    ? body.message
                    : `Attachment notification failed (${response.status}).`;
            throw new Error(message);
        }

        log.debug("notifyAddedLeaveRequestAttachment", ids.join(","));
    },

    removeLeaveRequestAttachment: async (id: number) => {
        await ensureFeathersReady();
        const attachment = await getFeathersClient()
            .service("leave-request-attachment")
            .remove(id);
        log.debug("removeLeaveRequestAttachment", JSON.stringify(attachment));
        return attachment;
    },

    downloadLeaveRequestAttachment: async (attachment: LeaveRequestAttachment) => {
        await ensureFeathersReady();
        const token = await authStorage.getItem("feathers-jwt");

        if (!token) {
            throw new Error("Missing authentication token.");
        }

        const serverUrl = getConfig("s4pServerUrl")?.replace(/\/$/, "");

        if (!serverUrl) {
            throw new Error("s4pServerUrl is not configured");
        }

        const url = `${serverUrl}/leave-request-attachment/download/${attachment.id}`;
        const headers = { Authorization: `Bearer ${token}` };

        if (Platform.OS === "web") {
            const response = await fetch(url, { headers });

            if (!response.ok) {
                throw new Error(`Download failed (${response.status}).`);
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = objectUrl;
            anchor.download = attachment.original_filename;
            anchor.rel = "noopener";
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(objectUrl);
            return;
        }

        const safeName = attachment.original_filename.replace(/[^\w.\-]+/g, "_");
        const targetUri = `${FileSystem.cacheDirectory ?? ""}${Date.now()}-${safeName}`;
        const result = await FileSystem.downloadAsync(url, targetUri, { headers });

        if (result.status !== 200) {
            throw new Error(`Download failed (${result.status}).`);
        }

        // Android blocks exposing raw file:// URIs to other apps (FileUriExposedException).
        // Convert to a FileProvider content:// URI and open via ACTION_VIEW.
        if (Platform.OS === "android") {
            const contentUri = await FileSystem.getContentUriAsync(result.uri);
            await IntentLauncher.startActivityAsync("android.intent.action.VIEW", {
                data: contentUri,
                flags: 1, // FLAG_GRANT_READ_URI_PERMISSION
                type: attachment.mime_type,
            });
            return;
        }

        // iOS rejects Linking.openURL for local file:// URLs. Quick Look is the
        // system viewer other apps use after a download (Mail, Files, WhatsApp).
        await openFilePreview(result.uri);
    },

    getMedicalRequestsForChildren: async (
        data?: unknown,
    ): Promise<MedicalRequest[]> => {
        const childIds = data as number[];

        if (childIds.length === 0) {
            return [];
        }

        await ensureFeathersReady();
        const medicalRequestsByChild = await Promise.all(
            childIds.map((childId) =>
                getFeathersClient()
                    .service("medical-request")
                    .find({ query: { child_id: childId } }),
            ),
        );
        const medicalRequests = medicalRequestsByChild.flat();
        log.debug(
            "getMedicalRequestsForChildren",
            JSON.stringify(medicalRequests),
        );
        return medicalRequests;
    },
    getMedicalRequest: async (id: number): Promise<MedicalRequest> => {
        await ensureFeathersReady();
        const medicalRequest = await getFeathersClient()
            .service("medical-request")
            .get(id);
        log.debug("getMedicalRequest", JSON.stringify(medicalRequest));
        return medicalRequest;
    },
    createMedicalRequest: async (data: MedicalRequestData) => {
        await ensureFeathersReady();
        const medicalRequest = await getFeathersClient()
            .service("medical-request")
            .create(data);
        log.debug("createMedicalRequest", JSON.stringify(medicalRequest));
        return medicalRequest;
    },
    updateMedicalRequest: async ({ id, note }: UpdateMedicalRequestArgs) => {
        await ensureFeathersReady();
        const data: MedicalRequestPatch = { note };
        const medicalRequest = await getFeathersClient()
            .service("medical-request")
            .patch(id, data);
        log.debug("updateMedicalRequest", JSON.stringify(medicalRequest));
        return medicalRequest;
    },
    revokeMedicalRequest: async (id: number) => {
        await ensureFeathersReady();
        const medicalRequest = await getFeathersClient()
            .service("medical-request")
            .remove(id);
        log.debug("revokeMedicalRequest", JSON.stringify(medicalRequest));
        return medicalRequest;
    },

    getPickupRequestsForChildren: async (
        data?: unknown,
    ): Promise<PickupRequest[]> => {
        const childIds = data as number[];

        if (childIds.length === 0) {
            return [];
        }

        await ensureFeathersReady();
        const pickupRequestsByChild = await Promise.all(
            childIds.map((childId) =>
                getFeathersClient()
                    .service("pickup-request")
                    .find({ query: { child_id: childId } }),
            ),
        );
        const pickupRequests = pickupRequestsByChild.flat();
        log.debug(
            "getPickupRequestsForChildren",
            JSON.stringify(pickupRequests),
        );
        return pickupRequests;
    },
    createPickupRequest: async (data: PickupRequestData) => {
        await ensureFeathersReady();
        const pickupRequest = await getFeathersClient()
            .service("pickup-request")
            .create(data);
        log.debug("createPickupRequest", JSON.stringify(pickupRequest));
        return Array.isArray(pickupRequest) ? pickupRequest : [pickupRequest];
    },
    revokePickupRequest: async (id: number) => {
        await ensureFeathersReady();
        const pickupRequest = await getFeathersClient()
            .service("pickup-request")
            .remove(id);
        log.debug("revokePickupRequest", JSON.stringify(pickupRequest));
        return pickupRequest;
    },

    getSchoolYear: async (userId: number): Promise<SchoolYear> => {
        await ensureFeathersReady();
        const schoolYear = await getFeathersClient()
            .service("school-year")
            .get(userId);
        log.debug("getSchoolYear", JSON.stringify(schoolYear));
        return schoolYear;
    },

    getProfiles: async (): Promise<Profile[]> => {
        await ensureFeathersReady();
        const profiles = await getFeathersClient()
            .service("profile")
            .find({ query: profileLangQuery() });
        log.debug("getProfiles", JSON.stringify(profiles));
        return profiles;
    },

    getProfile: async (id: number): Promise<Profile> => {
        await ensureFeathersReady();
        const profile = await getFeathersClient()
            .service("profile")
            .get(id, { query: profileLangQuery() });
        log.debug("getProfile", JSON.stringify(profile));
        return profile;
    },

    patchProfile: async ({ id, data }: PatchProfileArgs): Promise<Profile> => {
        await ensureFeathersReady();
        const profile = await getFeathersClient()
            .service("profile")
            .patch(id, data, { query: profileLangQuery() });
        log.debug("patchProfile", JSON.stringify(profile));
        return profile;
    },

    getParentSessions: async (): Promise<ParentSession[]> => {
        await ensureFeathersReady();
        const sessions = await getFeathersClient()
            .service("parent-session")
            .find();
        log.debug("getParentSessions", JSON.stringify(sessions));
        return sessions;
    },

    revokeParentSession: async (id: number): Promise<ParentSession> => {
        await ensureFeathersReady();
        const session = await getFeathersClient()
            .service("parent-session")
            .remove(id);
        log.debug("revokeParentSession", JSON.stringify(session));
        return session as ParentSession;
    },

    revokeAllParentSessions: async (): Promise<ParentSession[]> => {
        await ensureFeathersReady();
        const sessions = await getFeathersClient()
            .service("parent-session")
            .remove(null);
        log.debug("revokeAllParentSessions", JSON.stringify(sessions));
        return sessions as ParentSession[];
    },
};
